import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FileCard = ({ file, isSelected, onSelect, onPreview, onDownload, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getFileIcon = (type) => {
    switch (type) {
      case 'image':
        return 'Image';
      case 'video':
        return 'Video';
      case 'cad':
        return 'Box';
      case 'document':
        return 'FileText';
      default:
        return 'File';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div
      className={`relative bg-card border border-border rounded-lg p-4 transition-luxury hover:elevation-2 cursor-pointer ${
        isSelected ? 'ring-2 ring-accent border-accent' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(file?.id)}
    >
      {/* Selection Checkbox */}
      <div className="absolute top-3 left-3 z-10">
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-luxury ${
            isSelected
              ? 'bg-accent border-accent' :'border-border bg-background hover:border-accent'
          }`}
        >
          {isSelected && (
            <Icon name="Check" size={12} color="var(--color-accent-foreground)" />
          )}
        </div>
      </div>
      {/* File Preview */}
      <div className="relative mb-4 aspect-square bg-muted rounded-lg overflow-hidden">
        {file?.type === 'image' ? (
          <Image
            src={file?.thumbnail}
            alt={file?.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon
              name={getFileIcon(file?.type)}
              size={48}
              color="var(--color-muted-foreground)"
            />
          </div>
        )}

        {/* Overlay Actions */}
        {isHovered && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e?.stopPropagation();
                onPreview(file);
              }}
            >
              <Icon name="Eye" size={16} />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e?.stopPropagation();
                onDownload(file);
              }}
            >
              <Icon name="Download" size={16} />
            </Button>
          </div>
        )}

        {/* File Type Badge */}
        <div className="absolute top-2 right-2">
          <span className="px-2 py-1 bg-background/90 backdrop-blur-sm text-xs font-medium text-foreground rounded-full">
            {file?.type?.toUpperCase()}
          </span>
        </div>
      </div>
      {/* File Info */}
      <div className="space-y-2">
        <h3 className="font-medium text-foreground truncate" title={file?.name}>
          {file?.name}
        </h3>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{formatFileSize(file?.size)}</span>
          <span>{formatDate(file?.uploadDate)}</span>
        </div>

        {file?.project && (
          <div className="flex items-center space-x-1 text-xs text-accent">
            <Icon name="Folder" size={12} />
            <span className="truncate">{file?.project}</span>
          </div>
        )}

        {/* Security Status */}
        <div className="flex items-center space-x-1 text-xs">
          <Icon name="Shield" size={12} color="var(--color-success)" />
          <span className="text-success">Encrypted</span>
        </div>
      </div>
      {/* Quick Actions Menu */}
      <div className="absolute top-3 right-3">
        <Button
          variant="ghost"
          size="icon"
          className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-luxury"
          onClick={(e) => {
            e?.stopPropagation();
            // Handle menu toggle
          }}
        >
          <Icon name="MoreVertical" size={14} />
        </Button>
      </div>
    </div>
  );
};

export default FileCard;